import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { PatchContributorDto } from '@/types/dtos/contributors/request/patch-contributor.dto.ts';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '../../queries/contributors/use-get-event-contributors.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import { GET_USER_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/user-contributors.ts';

export const usePatchContributorMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patchContributorData: PatchContributorDto) => {
      const token = await getToken();
      return patchContributor(patchContributorData, token ?? '');
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY] }),
      ]);
    },
  });
};

const patchContributor = async (patchContributorData: PatchContributorDto, token: string): Promise<ContributorDto> => {
  const url = `${import.meta.env.VITE_API_URL}/contributors/${patchContributorData.id}`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body: patchContributorData, token });
};

export default usePatchContributorMutation;
